<template>
    <div>
        <div class="d-flex align-items-center justify-content-between mb-3">
            <h1>Studies</h1>

            <div>
                <button
                    class="btn btn-primary float-right"
                    @click="startCreate"
                >
                    Create Study
                </button>
            </div>
        </div>

        <study-modal
            :study="modal.study"
            @save="saveStudy"
        />

        <patient-enrollment-modal
            :study="modal.study"
            @save="savePatientEnrollment"
        />

        <delete-warning-modal
            title="Are you sure you want to delete this study?"
            description="This can not be reversed."
            @delete="deleteStudy"
        />

        <embed-token-modal :embed-token="modal.embedToken" />

        <table class="table table-hover">
            <thead>
                <tr>
                    <th
                        scope="col"
                        style="width: 30%"
                    >
                        Name
                    </th>

                    <th
                        scope="col"
                        style="width: 30%"
                    >
                        Domain
                    </th>

                    <th
                        scope="col"
                        style="width: 15%"
                    >
                        Patient Enrollment
                    </th>

                    <th
                        style="width: 10%"
                        scope="col"
                    >
                        &nbsp;
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="study in studies"
                    :key="study.id"
                >
                    <td>{{ study.name }}</td>
                    <td>
                        <a
                            v-if="study.domain"
                            :href="'https://' + study.domain"
                            target="_blank"
                        >{{ study.domain }}</a>

                        <span v-else>
                            N/A
                        </span>
                    </td>
                    <td>
                        {{ getPatientCount(study) }} / {{ study.patientsToBeIncluded }}
                    </td>
                    <td>
                        <font-awesome-icon
                            icon="code"
                            class="clickable"
                            @click="showEmbedCode(study)"
                        />

                        <font-awesome-icon
                            icon="users"
                            class="clickable ml-2"
                            @click="openPatientEnrollment(study)"
                        />

                        <font-awesome-icon
                            icon="edit"
                            class="clickable ml-2"
                            @click="startEdit(study)"
                        />

                        <font-awesome-icon
                            icon="trash"
                            class="clickable ml-2"
                            @click="startDelete(study)"
                        />
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import moment from 'moment';
import utils from '../utils';
import studyService from '../services/study.service';
import studyStatuses from '../data/study-statuses';
import DeleteWarningModal from '../components/DeleteWarningModal.vue';
import StudyModal from '../components/StudyModal.vue';
import PatientEnrollmentModal from '../components/PatientEnrollmentModal.vue';
import EmbedTokenModal from '../components/EmbedTokenModal.vue';

export default {
    name: 'Studies',

    components: {
        EmbedTokenModal,
        DeleteWarningModal,
        PatientEnrollmentModal,
        StudyModal,
    },

    data() {
        return {
            isLoading: false,
            studies: [],
            modal: {
                study: {},
                embedToken: '',
            },
            studyStatuses,
        };
    },

    mounted() {
        this.getStudies().then((r) => {
            this.studies = r.data;
            this.isLoading = false;
        });
    },

    methods: {
        getStudies: studyService.getStudies,

        startCreate() {
            this.modal.study = {
                name: '',
                description: '',
                population: '',
                primaryOutcome: '',
                secondaryOutcome: '',
                domain: '',
                studyStatus: this.studyStatuses[0].value,
                patientsToBeIncluded: 100,
                lplvQuarter: 1,
                lplvYear: moment().year(),
            };

            this.$modal.show('study');
        },

        startEdit(study) {
            this.modal.study = study;

            this.$modal.show('study');
        },

        startDelete(study) {
            this.modal.study = study;

            this.$modal.show('delete-warning');
        },

        openPatientEnrollment(study) {
            this.modal.study = study;

            this.$modal.show('patient-enrollment');
        },

        async savePatientEnrollment() {
            const enrollments = [...this.modal.study.patientEnrollments];

            this._.each(enrollments, (e) => {
                delete e.group;
            });

            await studyService.updatePatientEnrollment(this.modal.study.id, enrollments).then(() => {
                this.$modal.hide('patient-enrollment');

                this.$toastr.s('The enrollment data was updated successfully.');
            }).catch(() => {
                this.$toastr.e('Unable to update enrollment data, please try again...');
            });
        },

        async saveStudy() {
            this.sanitizeDomain();

            if (this.modal.study.id == null) {
                await studyService.createStudy(this.modal.study).then((r) => {
                    this.modal.study.id = r.data.id;

                    this.studies.push(this.modal.study);

                    this.$modal.hide('study');

                    this.$toastr.s('The study was created successfully.');
                }).catch(() => {
                    this.$toastr.e('Unable to create the study, please try again...');
                });

                return;
            }

            await studyService.updateStudy(this.modal.study).then(() => {
                this.$modal.hide('study');

                this.$toastr.s('The study was updated successfully.');
            }).catch(() => {
                this.$toastr.e('Unable to update the study, please try again...');
            });
        },

        async deleteStudy() {
            await studyService.deleteStudy(this.modal.study.id).then(() => {
                this.studies = this._.filter(this.studies, (s) => s.id !== this.modal.study.id);

                this.$modal.hide('delete-warning');

                this.$toastr.s('The study was deleted successfully.');
            }).catch(() => {
                this.$toastr.e('Unable to delete the study, please try again...');
            });
        },

        getStatusLabel(statusValue) {
            const status = this._.find(this.studyStatuses, (ss) => ss.value === statusValue);

            if (status == null) {
                return 'Onbekend';
            }

            return status.label;
        },

        getPatientCount: utils.getPatientCount,

        sanitizeDomain() {
            if (this.modal.study.domain.startsWith('https://') || this.modal.study.domain.startsWith('http://')) {
                const { hostname } = new URL(this.modal.study.domain);

                this.modal.study.domain = hostname;
            }
        },

        showEmbedCode(study) {
            const params = new URLSearchParams();

            if (study.name) {
                params.append('name', study.name);
            }

            if (study.domain) {
                params.append('domain', study.domain);
            }

            if (study.embedToken) {
                params.append('token', study.embedToken);
            }

            this.modal.embedToken = `<iframe width="100%" height="1200px;" frameBorder="0" src="https://${window.location.host}/dashboard?${params.toString()}">Browser not compatible.</iframe>`;

            this.$modal.show('embed-token');
        },
    },
};
</script>
